import { useJwt } from '@vueuse/integrations/useJwt';

const USER_PLACEHOLDER_EMAIL = 'hw_placeholder_email';
const FACEBOOK_PICTURE = 'https://hostelworld.com/uploaded_picture';

export function useAccount() {
  const nuxtApp = useNuxtApp();
  const runtimeConfig = useRuntimeConfig();
  const envData = runtimeConfig?.public;
  const { sendUsersUpdateEvent } = useApiIterable();
  const { useImage } = useImages();
  const { createLoginCookies } = useLoginCookies();

  // ### COOKIES - Using standardized configuration with proper maxAge values
  // AI Note: These are default maxAge values, actual values are set when tokens are received
  const loginCookies = createLoginCookies();
  const userLoginPlaceholderEmail = useCookie(USER_PLACEHOLDER_EMAIL, {
    domain: envData?.COOKIE_DOMAIN,
    path: '/',
    secure: true,
    sameSite: 'strict',
    maxAge: 34560000,
    watch: true,
  });

  // ### COMPOSABLES
  const { useUrl } = useUrls();

  // ### STATIC VALUES
  const loggedInUser = ref(null);
  const translation = nuxtApp?.$i18n;

  // ### COMPUTED / GETTERS
  const hasLoggedInUser = computed(() => {
    return !!toValue(loggedInUser)?.email;
  });

  const getLoggedInUser = computed(() => {
    return hasLoggedInUser ? toValue(loggedInUser) : null;
  });

  const getLoggedInMenuItems = computed(() => {
    if (!hasLoggedInUser) {
      return [];
    }

    return {
      account: {
        label: translation?.t('t_ACCOUNT'),
        href: useUrl.getMyAccountUrl('account'),
        icon: 'home',
      },
      details: {
        label: translation?.t('t_EDITDETAILS'),
        href: useUrl.getMyAccountUrl('account/details'),
        icon: 'user-line',
      },
      wallet: {
        label: translation?.t('t_WALLET'),
        href: useUrl.getMyAccountUrl('account/wallet'),
        icon: 'wallet',
      },
      trips: {
        label: translation?.t('t_MYTRIPS'),
        href: useUrl.getMyAccountUrl('account/trips'),
        icon: 'map',
      },
      help: {
        label: translation?.t('t_HELP'),
        href: translation?.t('t_CONTACTUSURL'),
        icon: 'question',
        rel: 'noopener noreferrer',
        target: '_blank',
      },
      logout: {
        label: translation?.t('t_LOGOUT'),
        icon: 'check-out',
      },
    };
  });

  // ### METHODS / ACTIONS
  const setLoggedInUser = async function () {
    const userEncodedJwt = toValue(loginCookies.idToken);

    if (userEncodedJwt) {
      const userData = useJwt(userEncodedJwt);
      const userInfo = toValue(userData.payload);

      if (!userInfo) {
        return;
      }

      let url = userInfo?.[FACEBOOK_PICTURE] || userInfo?.picture || '';
      loggedInUser.value = {
        name: userInfo?.name,
        email: userInfo?.email,
        picture: url,
        token: userEncodedJwt,
      };
      // Perform image validation asynchronously
      if (true && url) {
        const isValid = await useImage.isImageUrlValid(url);
        url = isValid ? url : '';
        // Update the photo property after validation
        loggedInUser.value.picture = url;
      }
    }
  };

  const refreshUserToken = async function () {
    if (!toValue(hasLoggedInUser)) {
      return;
    }

    // Dynamic import avoids Vitest auto-import pulling useAuth0TokenRefresh → #app into every suite
    const { useAuth0TokenRefresh } = await import('@/composables/useAuth0TokenRefresh.js');
    const { tryRefreshTokens } = useAuth0TokenRefresh();
    await tryRefreshTokens();
  };

  const createNewPlaceholderEmail = function (sessionStore) {
    if (!sessionStore) {
      console.error('### Missing session store when creating placeholder email');
      return;
    }

    const genPlaceholderEmail = `${crypto.randomUUID()}.pwa@placeholder.email`;

    userLoginPlaceholderEmail.value = genPlaceholderEmail;
    sessionStore.setPlaceholderEmail(genPlaceholderEmail);

    return genPlaceholderEmail;
  };

  const generateNewPlaceholderEmail = async function () {
    const { isTrackingAllowed } = checkGdpr();

    if (!isTrackingAllowed) {
      // The user has not agreed to be tracked
      return;
    }

    const sessionStore = useSessionStore();
    const genPlaceholderEmail = createNewPlaceholderEmail(sessionStore);

    if (!genPlaceholderEmail) {
      return;
    }

    // Send an "/api/users/update" call to Iterable
    try {
      await sendUsersUpdateEvent({ email: genPlaceholderEmail });
    } catch (err) {
      console.error('### Failed to send users update event for placeholder email', err);
    }

    return genPlaceholderEmail;
  };

  const getRealOrPlaceholderEmail = function () {
    const sessionStore = useSessionStore();

    // Get the email from the session if it is there
    if (sessionStore.getPlaceholderEmail()) {
      return sessionStore.getPlaceholderEmail();
    }

    // Get the email from the cookie if it is there
    const placeholderCookie = useCookie(USER_PLACEHOLDER_EMAIL, { domain: envData?.COOKIE_DOMAIN });

    if (placeholderCookie.value) {
      sessionStore.setPlaceholderEmail(placeholderCookie.value);
      return placeholderCookie.value;
    }

    const { isTrackingAllowed } = checkGdpr();
    if (!isTrackingAllowed) {
      return;
    }

    const genPlaceholderEmail = createNewPlaceholderEmail(sessionStore);
    if (!genPlaceholderEmail) {
      return;
    }

    sendUsersUpdateEvent({ email: genPlaceholderEmail }).catch((err) => {
      console.error('### Failed to send users update event for placeholder email', err);
    });

    return genPlaceholderEmail;
  };

  const logout = async function () {
    const sessionStore = useSessionStore();
    const { clearAuthSession } = useClearAuthSession();

    await clearAuthSession();

    await generateNewPlaceholderEmail();

    const nationalityStorage = useLocalStorage('nationality');
    nationalityStorage.value = null;

    const auth0 = sessionStore.getAuth0();

    if (auth0) {
      auth0.logout({ returnTo: useUrl.getAuth0CallbackPageUrl() });
    }
  };

  // ### TRY TO FETCH THE LOGGED-IN USER INFORMATION
  setLoggedInUser();

  return {
    hasLoggedInUser,
    getLoggedInUser,
    getLoggedInMenuItems,
    generateNewPlaceholderEmail,
    getRealOrPlaceholderEmail,
    logout,
    refreshUserToken,
  };
}
